import { capitalizeAll } from '@moderepo/mode-app-components';
import { userAppDataContext } from '@moderepo/mode-data-state-user-app';
import React, { CSSProperties, useCallback, useContext, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import { Alert, AlertTargetMetric, isMetricsHeartbeatAlertCondition, isMetricsThresholdAlertCondition, MetricsAlertConditionThreshold }
    from '@moderepo/mode-internal-apis';
import { getAlertConditionTypeDisplayName, getAlertSeverityDisplayName, getAlertStateDisplayName, getAlertTargetByDisplayName,
    getEntityClassDisplayValue } from '.';
import { TargetMetricFields, ThresholdFields, AlertInfoProps, AlertInfo } from '../../components/alerting';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectAlert, useSelectProject } from '../../utils/dataStateHooks';
import { getDateTimeDisplay } from '../containerUtils';
import { getTimeSeriesAggregationTypeName } from '../time-series/tsUtils';
import { getEntityDisplayName, useSelectTranslatedEntityClasses } from '..';



export interface AlertInfoContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly alertId: string;
}



/**
 * This container shows the AlertInfo component in readonly view.
 */
export const AlertInfoContainer: React.FC<AlertInfoContainerProps> = (props: AlertInfoContainerProps) => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);


    // Get the alert object for the specified alertId
    const selectedAlert = useSelectAlert(props.projectId, props.alertId);



    /**
     * Helper function to create the fields that display the attributes for targetMetric
     */
    const getTargetMetricFields = useCallback((targetMetric: AlertTargetMetric): TargetMetricFields => {
        const fields: TargetMetricFields = {
            entityClass: {
                label       : capitalizeAll(trans('field.entity_class.label')),
                displayValue: getEntityClassDisplayValue(targetMetric.entityClass, entityClasses),
            },
            metricsDefinitionId: {
                label       : capitalizeAll(trans('field.metrics_definition_id.label')),
                displayValue: targetMetric.metricsDefinitionId,
            },
            metricName: {
                label       : capitalizeAll(trans('alerting.label.metric_name')),
                displayValue: targetMetric.metricName ?? '',
            },
            targetBy: targetMetric.entityTargetCondition ? {
                label       : capitalizeAll(trans('alerting.label.target_by')),
                displayValue: targetMetric.entityTargetCondition.targetBy
                    ? capitalizeAll(trans(getAlertTargetByDisplayName(targetMetric.entityTargetCondition?.targetBy)))
                    : '',
            } : undefined,
            entityIds: targetMetric.entityTargetCondition ? {
                label       : capitalizeAll(trans('terms.entity_other')),
                displayValue: targetMetric.entityTargetCondition?.entityIds.map((entityId) => {
                    return getEntityDisplayName(props.projectId, entityId, appDataState, appDataDispatch);
                }).join(', ') ?? '',
            } : undefined,
            exclude: targetMetric.entityTargetCondition ? {
                label       : capitalizeAll(trans('exclude')),
                displayValue: targetMetric.entityTargetCondition?.exclude ? 'true' : 'false',
            } : undefined,
        };

        return fields;
    }, [entityClasses, props.projectId, trans, appDataDispatch, appDataState]);



    /**
     * Helper function to create the fields that display the attributes for condition threshold
     */
    const getConditionThresholdFields = useCallback((threshold: MetricsAlertConditionThreshold): ThresholdFields => {
        return {
            severity: {
                label       : capitalizeAll(trans('alerting.label.severity')),
                value       : threshold.severity,
                displayValue: capitalizeAll(trans(getAlertSeverityDisplayName(threshold.severity))),
            },
            operator: {
                label       : capitalizeAll(trans('operator')),
                displayValue: threshold.operator,
            },
            operands: {
                label       : capitalizeAll(trans('operands')),
                displayValue: threshold.operands.join(', '),
            },
        };
    }, [trans]);


    /**
     * Helper function to create the fields that display the attributes for condition
     */
    const getConditionFields = useCallback((alert: Alert): AlertInfoProps['fields']['alertRule']['condition'] => {
        if (isMetricsHeartbeatAlertCondition(alert.condition)) {
            return {
                severity: {
                    label       : capitalizeAll(trans('alerting.label.severity')),
                    value       : alert.condition.severity,
                    displayValue: capitalizeAll(trans(getAlertSeverityDisplayName(alert.condition.severity))),
                },
                targetMetric: getTargetMetricFields(alert.condition.metricsCondition),
            };
        }
        if (isMetricsThresholdAlertCondition(alert.condition)) {
            return {
                aggregation: {
                    label       : capitalizeAll(trans('alerting.label.aggregation')),
                    displayValue: capitalizeAll(trans(getTimeSeriesAggregationTypeName(alert.condition.aggregation))),
                },
                targetMetric: getTargetMetricFields(alert.condition.metricsCondition),
                thresholds  : alert.condition.thresholds.map((threshold) => {
                    return getConditionThresholdFields(threshold);
                }),
            };
        }
        return undefined;
    }, [getConditionThresholdFields, getTargetMetricFields, trans]);



    /**
     * Set up ALL the fields for the Alert info component and cache it. These are the fields for showing the alert's attributes
     * e.g. id, name, description, targets, conditions, etc.... This will be passed to the AlertInfo component later.
     */
    const alertCompFields = useMemo((): AlertInfoProps['fields'] | undefined => {

        if (selectedAlert) {

            return {
                id: {
                    label       : capitalizeAll(trans('label.id')),
                    displayValue: selectedAlert.id.toString(),
                },
                type: {
                    label       : capitalizeAll(trans('alerting.alert_condition_type')),
                    value       : selectedAlert.condition.conditionType,
                    displayValue: capitalizeAll(trans(
                        getAlertConditionTypeDisplayName(selectedAlert.condition.conditionType) ?? 'label.unknown',
                    )),
                },
                description: {
                    label       : capitalizeAll(trans('label.description')),
                    displayValue: selectedAlert.description,
                },
                entityId: {
                    label       : capitalizeAll(trans('terms.entity')),
                    value       : selectedAlert.entityId,
                    displayValue: getEntityDisplayName(props.projectId, selectedAlert.entityId, appDataState, appDataDispatch, true),
                },
                severity: {
                    label       : capitalizeAll(trans('label.severity')),
                    value       : selectedAlert.severity,
                    displayValue: capitalizeAll(trans(getAlertSeverityDisplayName(selectedAlert.severity))),
                },
                state: {
                    label       : capitalizeAll(trans('label.state')),
                    value       : selectedAlert.state,
                    displayValue: capitalizeAll(trans(getAlertStateDisplayName(selectedAlert.state))),
                },
                recoveryTime: {
                    label       : capitalizeAll(trans('alerting.label.recovered_date')),
                    displayValue: selectedAlert.recoveryTime
                        ? getDateTimeDisplay(selectedAlert.recoveryTime, trans)
                        : undefined,
                },
                invocationTime: {
                    label       : capitalizeAll(trans('alerting.label.invocation_date')),
                    displayValue: getDateTimeDisplay(selectedAlert.invocationTime, trans),
                },
                alertRule: {
                    condition: getConditionFields(selectedAlert),
                },
            };
        }

        return undefined;
    }, [appDataDispatch, appDataState, getConditionFields, props.projectId, selectedAlert, trans]);



    /**
     * Set up the texts for the Alert info component and cache it. Will pass this to the AlertInfo component later
     */
    const alertCompTexts = useMemo((): AlertInfoProps['texts'] => {
        return {
            alertRuleTitle      : capitalizeAll(trans('alerting.label.triggered_by_alert_rule')),
            alertConditionsTitle: capitalizeAll(trans('alerting.label.condition')),
            metricTargetTitle   : capitalizeAll(trans('alerting.label.target')),
            metricTargetsTitle  : capitalizeAll(trans('alerting.label.target_other')),
        };
    }, [trans]);



    return (
        <>
            {alertCompFields && (
                <AlertInfo
                    className={props.className}
                    style={props.style}
                    title={capitalizeAll(trans('alerting.alert.into_panel.title'))}
                    icon="notifications_active"
                    fields={alertCompFields}
                    texts={alertCompTexts}
                />
            )}

            {!alertCompFields && (
                <CircularProgress />
            )}
        </>
    );
};
